/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import clsx from 'clsx'
import { useFormik } from 'formik'
import { login, developerLogin, loginVerify } from '../core/_requests'
import { useAuth } from '../core/Auth'
import { getDashboardPath } from '../../../utils/helper'
import { KTIcon } from '../../../../_payairo/helpers'
import { generateAndSaveFCMToken } from '../../../../lib/firebase'

const loginSchema = Yup.object().shape({
  adminId: Yup.string()
    .min(3, 'Minimum 3 symbols')
  // .email('Email is required'),
})

const initialValues = {
  adminId: '',
  password: ''
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { saveAuth, setCurrentUser } = useAuth()
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        let authResponse;
        let isDeveloper = false;

        // Step 1: POST /auth/useradmin/login/ — Admin, Super Admin, Manager, Customer Support, etc.
        try {
          const { data } = await login(values.adminId, values.password)
          if (data.status !== false) {
            authResponse = data;
          }
        } catch (e) {
          console.log('Standard login failed, trying developer fallback...')
        }

        // Step 2: Fallback to Developer login if needed
        if (!authResponse) {
          try {
            const { data } = await developerLogin(values.adminId, values.password)
            if (data.status !== false) {
              authResponse = data;
              isDeveloper = true;
            }
          } catch (e) {
            console.error('Developer login also failed')
          }
        }

        if (!authResponse || authResponse.status === false) {
          setLoading(false)
          setStatus('The login details are incorrect')
          setSubmitting(false)
          return
        }

        const auth = authResponse;
        setLoading(false)
        console.log('Login Success:', auth.data)
        
        // Extract user data from response
        const userData = auth.data?.user || auth.data
        const userType = userData?.user_type || (isDeveloper ? 'Developer' : 'Admin')
        const role = userData?.role || auth.data?.role || userType
        const userId = userData?.id || auth.data?.userId || userData?.user_id
        const token = auth.data?.access_token || auth.data?.token
        const profileData = userData?.profileData || auth.data?.profileData
        
        // Store in localStorage as requested
        localStorage.setItem("access_token", token || '')
        localStorage.setItem("refresh_token", auth.data?.refresh_token || '')
        localStorage.setItem("user_type", userType)
        localStorage.setItem("user", JSON.stringify(userData))

        // Store complete auth data with role-based fields
        const authData = {
          ...auth.data,
          token: token,
          userId: userId,
          role: role,
          user_type: userType,
          profileData: profileData,
          user: {
            ...userData,
            role: role,
            user_type: userType,
            profileData: profileData,
          }
        }

        // Clear previous user state before setting new auth - prevents role flicker
        setCurrentUser(undefined)
        saveAuth(authData)
        setCurrentUser({
          ...userData,
          role: role,
          user_type: userType,
          profileData: profileData,
        })
        
        // ✅ FCM Token generation
        try {
          await generateAndSaveFCMToken()
        } catch (e) {
          console.warn('FCM registration failed', e)
        }
        
        // Get role (priority) or fallback to user_type for backward compatibility
        const userRole = role || userType
        console.log('User Role:', userRole)

        // Manager → /manager/dashboard | Spectator/Developer → /spectator/dashboard
        const redirectPath = getDashboardPath(userRole, userType)
        console.log('Redirecting to:', redirectPath)
        
        navigate(redirectPath, { replace: true })
      } catch (error) {
        console.error(error)
        saveAuth(undefined)
        setStatus('The login details are incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const theme = localStorage.getItem('kt_theme_mode_value')

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {/* begin::Heading */}
      <div className='text-center mb-11'>
        {theme == 'light'
          ? <img src='/media/logos/payairo-color.png' className='w-100' />
          : <img src='/media/logos/payairo-white.png' className='w-100' />
        }
      </div>

      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>Sign In</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          Please enter the admin ID to login
        </div>
      </div>
      {/* begin::Heading */}

      {formik.status ? (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      ) : (
        ''
      )}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
        <input
          placeholder='Email'
          {...formik.getFieldProps('adminId')}
          className={clsx(
            'form-control bg-transparent',
            { 'is-invalid': formik.touched.adminId && formik.errors.adminId },
            {
              'is-valid': formik.touched.adminId && !formik.errors.adminId,
            }
          )}
          type='text'
          name='adminId'
          autoComplete='off'
        />
        {formik.touched.adminId && formik.errors.adminId && (
          <div className='fv-plugins-message-container text-danger'>
            <span role='alert'>{formik.errors.adminId}</span>
          </div>
        )}
      </div>

      <div className='fv-row mb-3'>
        <label className='form-label fs-6 fw-bolder text-dark'>Password</label>
        <div className='position-relative'>
          <input
            placeholder='Password'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent pe-10',
              { 'is-invalid': formik.touched.password && formik.errors.password },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
            type={showPassword ? 'text' : 'password'}
            name='password'
            autoComplete='off'
          />
          <button
            type='button'
            className='btn btn-sm btn-icon position-absolute top-50 end-0 translate-middle-y me-2'
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={-1}
          >
            <KTIcon iconName={showPassword ? 'eye-slash' : 'eye'} className='fs-2 text-white' />
          </button>
        </div>
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container text-danger'>
            <span role='alert'>{formik.errors.password}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>Login</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}

      <div className='text-gray-500 text-center fw-semibold fs-6'>
        Need any help ?{' '}
        <a href='https://payairo.com/contactus.html' className='link-primary' target='_blank'>
          Contact Us
        </a>
      </div>
    </form>
  )
}
